"use client";

import {JSX} from "react";

import {LocationSearchView} from "@/features/locations";
import {GuestsButtonSm, GuestsView} from "@/features/rental/guests";
import {type Payload, SimpleSearch, type SimpleSearchInstance} from "@/features/rental/simple-search/simple-search.model";

import {useRouterBridge} from "@/shared/hooks";
import {useServerRouter} from "@/shared/hooks/navigation/use-server-router";
import {useModel} from "@/shared/hooks/state-management/use-model";
import {ButtonWithImage} from "@/shared/ui/buttons";
import {DateRangePickerField} from "@/shared/ui/form/datepickers";
import {getStaticPathIcon} from "@/shared/utils/get-static-path-icon";

import {HeaderDateTrigger} from "./header-date-trigger";

import "./styles/header-search.scss";

interface HeaderSearchProps {
    className?: string;
    params: Payload;
}

/**
 * Compact header search bar for destination sub-pages.
 * Shows: Location | Dates | Search button
 */
export function HeaderSearch({className, params}: HeaderSearchProps): JSX.Element {
    const [search] = useModel<SimpleSearchInstance, [Payload?]>(SimpleSearch, params);
    useRouterBridge(search, {skipInitialSync: true});
    useServerRouter(search);

    const handleSubmit = (): void => {
        search.submit();
    };

    return (
        <div className={`header-search ${className || ""}`}>
            <div className="header-search__wrap">
                <div className="header-search__fields">
                    <div className="header-search__field header-search__field--location">
                        <LocationSearchView
                            dropdownOffset={7}
                            model={search.locationSearch}
                            placeholder="Search"
                            className="header-search__location"
                        />
                    </div>
                    <div className="header-search__divider" />
                    <DateRangePickerField
                        model={search.dateRange}
                        startPlaceholder="Check in"
                        endPlaceholder="Check out"
                        showClearButton={false}
                        singleCalendar={true}
                        dropdownOffset={10}
                        dropdownZIndex={100}
                        placement="bottom"
                        renderTrigger={(props) => <HeaderDateTrigger {...props} />}
                        className="header-search__field header-search__field--dates"
                    />
                    <div className="header-search__divider" />
                    <GuestsView
                        dropdownOffset={10}
                        model={search.guests}
                        placement="bottom"
                        className="header-search__field header-search__field--guests"
                        TriggerButton={GuestsButtonSm}
                    />
                    <div className="header-search__divider" />
                    <ButtonWithImage onClick={handleSubmit} variant="primary" icon={getStaticPathIcon("search")} />
                </div>
            </div>
        </div>
    );
}
